import React from 'react';
import I18n from 'i18n-js';
import qs from 'qs';
import SanitizedRender from './sanitized_render';
import { TERMS_OF_USE_PATH, PRIVACY_POLICY_PATH, GDPR_POLICY_PATH } from '../url_helpers';
import CsrfForm from '../csrf_form';
import FormGroupWithCheckbox from './form_group_with_checkbox';
import FormGroupWithInput from './form_group_with_input';
import ThirdPartyAuthButtons from './third_party_auth_buttons';
import GoogleRecaptchaTerms from './google_recaptcha_terms';
import { GOOGLE_RECAPTCHA_SITE_KEY } from '@reverbdotcom/env';
import { GOOGLE_RECAPTCHA_ACTIONS, GOOGLE_RECAPTCHA_TOKEN_PARAM } from '../constants';
import { useExpEnabled, useUser } from '../user_hooks';
import experiments from '../experiments';

interface Props {
  redirectTo?: string;
  shouldRenderHeader?: boolean;
  variant: 'default' | 'continue';
}

function GdprExplanation() {
  const translation = I18n.t('commons.signupForm.gdprReverbProcessesData', {
    gdprPolicyLinkOpen: `<a target='_blank' href='${GDPR_POLICY_PATH}'>`,
    linkClose: '</a>',
  });

  return (
    <div className="signup-signin__form__gdpr__explanation">

      <hr className="mtb-1" />

      <h4 className="signup-signin__form__gdpr__explanation__title">
        {I18n.t('commons.signupForm.gdprHowReverbUsesData')}
      </h4>
      <p className="signup-signin__form__gdpr__explanation__subtext">
        <SanitizedRender html={translation} />
      </p>
    </div>
  );
}

function Header() {
  return (
    <h4 className="signup-signin__header">
      {I18n.t('commons.signupForm.signupHeader')}
    </h4>
  );
}

function GdprTerms(props: TermsCheckboxesProps) {
  return (
    <div className="signup-signin__form__gdpr">
      <TermsCheckboxes
        gdpr={props.gdpr}
        agreedToMarketingTerms={props.agreedToMarketingTerms}
        setAgreedToMarketingTerms={props.setAgreedToMarketingTerms}
      />
      <input type="hidden" name="user[terms_type]" value="gdpr" />
    </div>
  );
}

function AcceptTermsLabel(props: { gdpr: boolean }) {
  if (props.gdpr) {
    const gdprTranslation = I18n.t('commons.signupForm.gdprAcceptTermsLabel', {
      termsLinkOpen: `<a href='${TERMS_OF_USE_PATH}'>`,
      privacyLinkOpen: `<a href='${PRIVACY_POLICY_PATH}'>`,
      linkClose: '</a>',
    });
    return <SanitizedRender html={gdprTranslation} />;
  }

  const translation = I18n.t('commons.signupForm.acceptTermsLabel', {
    termsLinkOpen: `<a href='${TERMS_OF_USE_PATH}'>`,
    privacyLinkOpen: `<a href='${PRIVACY_POLICY_PATH}'>`,
    linkClose: '</a>',
  });
  return <SanitizedRender html={translation} />;
}


interface TermsCheckboxesProps {
  gdpr: boolean,
  agreedToMarketingTerms: boolean,
  setAgreedToMarketingTerms: (value) => void,
}

/**
   * Because Enzyme doesn't propogate the onSubmit callback testing
   * that I don't muck with these inputs is tough. According to mdn's
   * docs https://developer.mozilla.org/en-US/docs/Web/HTML/Element/input/checkbox
   *
   * A checkbox will send a value of 'on' or undefined. We don't, nor should we
   * coerece these into truthy values. On the Rails side we'll make sure that 'on'
   * and nil represet the falsy and truthy values that they should.
   *
   * So this will come across as `user[accept_terms]=on` in the FormParams
   * or `user[accept_terms]=` if it isn't set (or the value won't be sent at all)
   *
   * Here is the corresponding Rails PR:
   */
function TermsCheckboxes(props: TermsCheckboxesProps) {
  return (
    <div>
      {!props.agreedToMarketingTerms
        && <input type="hidden" name="user[weekly_newsletter]" value="false" className="hidden_weekly_newsletter" />}

      <FormGroupWithCheckbox
        label={I18n.t('commons.signupForm.newsletterLabel')}
        inputName="user[weekly_newsletter]"
        updateField={() => props.setAgreedToMarketingTerms(!props.agreedToMarketingTerms)}
        fieldName="agreedToMarketingTerms"
        checked={props.agreedToMarketingTerms}
      />

      <FormGroupWithCheckbox
        label={<AcceptTermsLabel gdpr={props.gdpr} />}
        inputName="user[accept_terms]"
        required
        tagRequired
      />
    </div>
  );
}

function formAction(redirectTo: string): string {
  const authUrlParams = qs.stringify({
    redirect_to: redirectTo,
  });
  return `/signup?${authUrlParams}`;
}

function emailsMatch(emailValue: string, emailConfirmValue: string) {
  // if the user hasn't entered values in the email fields
  // we don't want to annoy them with errors yet
  if (!emailValue || !emailConfirmValue) return true;
  return emailValue === emailConfirmValue;
}

export function SignupForm(props: Props) {
  const { gdpr } = useUser();

  const [emailValue, setEmailValue] = React.useState('');
  const [emailConfirmValue, setEmailConfirmValue] = React.useState('');
  const [agreedToMarketingTerms, setAgreedToMarketingTerms] = React.useState(!gdpr);
  const [hasBlurredEmailConfirm, setHasBlurredEmailConfirm] = React.useState(false);
  const googleRecaptchaTokenInput = React.useRef();

  const shouldUseGoogleRecaptcha = useExpEnabled(experiments.RENDER_GOOGLE_RECAPTCHA_SCRIPT);
  // only show "emails must match" error on blur of the email confirm field
  // we don't want errors popping up until they've at least attempted to write values
  const showEmailMismatchError = hasBlurredEmailConfirm && !emailsMatch(emailValue, emailConfirmValue);
  const emailsMatchErrorText = showEmailMismatchError ? I18n.t('commons.signupForm.emailMatchError') : null;

  const handleSubmit = (event): void => {
    event.preventDefault();

    const form = event.target;

    if (!shouldUseGoogleRecaptcha) {
      form.submit();
      return;
    }

    (window as any).grecaptcha.enterprise.ready(function () {
      (window as any).grecaptcha.enterprise.execute(GOOGLE_RECAPTCHA_SITE_KEY, { action: GOOGLE_RECAPTCHA_ACTIONS.SIGNUP }).then(function (token) {
        if (googleRecaptchaTokenInput.current) {
          (googleRecaptchaTokenInput.current as HTMLInputElement).value = token;
        }
        form.submit();
      });
    });
  };

  function buttonText() {
    if (props.variant === 'continue') {
      return I18n.t('commons.signupForm.continue');
    } else {
      return I18n.t('commons.signupForm.signUp');
    }
  }

  return (
    <div>
      {props.shouldRenderHeader && <Header />}
      <CsrfForm
        id="reverb-signup-form"
        action={formAction(props.redirectTo)}
        method="POST"
        data-disable-auto-validation="true" // block forms.js handling
        onSubmit={
          (event) => { handleSubmit(event); }
        }
      >
        <div className="g-container scaling-mb-2">
          <div className="g-col-6">
            <FormGroupWithInput
              label={I18n.t('commons.signupForm.firstNameLabel')}
              inputName="user[first_name]"
              autoComplete="given-name"
              autoFocus
              required
            />
          </div>
          <div className="g-col-6">
            <FormGroupWithInput
              label={I18n.t('commons.signupForm.lastNameLabel')}
              inputName="user[last_name]"
              autoComplete="family-name"
              required
            />
          </div>
        </div>

        <FormGroupWithInput
          label={I18n.t('commons.signupForm.emailLabel')}
          inputType="email"
          inputName="user[email]"
          fieldName="emailValue"
          autoComplete="email"
          value={emailValue}
          updateField={(fieldUpdate) => setEmailValue(fieldUpdate.emailValue)}
          required
        />

        <FormGroupWithInput
          label={I18n.t('commons.signupForm.emailConfirmLabel')}
          inputType="email"
          inputName="emailConfirmValue"
          autoComplete="email"
          errorText={emailsMatchErrorText}
          value={emailConfirmValue}
          updateField={(fieldUpdate) => setEmailConfirmValue(fieldUpdate.emailConfirmValue)}
          onBlur={() => { setHasBlurredEmailConfirm(true); }}
          required
        />

        <FormGroupWithInput
          label={I18n.t('commons.signupForm.passwordLabel')}
          helpText={I18n.t('commons.signupForm.passwordTip', { minLength: 8 })}
          inputName="user[password]"
          inputType="password"
          autoComplete="new-password"
          required
        />

        {
          gdpr &&
          <GdprTerms
            gdpr={gdpr}
            agreedToMarketingTerms={agreedToMarketingTerms}
            setAgreedToMarketingTerms={setAgreedToMarketingTerms}
          />
        }

        <div className="scaling-mtb-2">
          <input
            type="submit"
            value={buttonText()}
            className="button button--primary width-100"
            disabled={!emailsMatch(emailValue, emailConfirmValue)}
          />
          {shouldUseGoogleRecaptcha && <GoogleRecaptchaTerms />}
        </div>

        {
          !gdpr &&
          <div className="mtb-2">
            <TermsCheckboxes
              gdpr={gdpr}
              agreedToMarketingTerms={agreedToMarketingTerms}
              setAgreedToMarketingTerms={setAgreedToMarketingTerms}
            />
          </div>
        }

        {
          shouldUseGoogleRecaptcha &&
          <input type="hidden" name={`${GOOGLE_RECAPTCHA_TOKEN_PARAM}`} ref={googleRecaptchaTokenInput} />
        }

      </CsrfForm>
      {gdpr &&
        <div className="mb-2">
          <GdprExplanation />
        </div>
      }
      <ThirdPartyAuthButtons
        redirectTo={props.redirectTo}
        variant={props.variant === 'continue' ? 'continue' : 'sign-up'}
      />
    </div>
  );
}

export default SignupForm;
